import React from "react";

import { useGroups } from "../../api/hooks";

//services
import { Group } from "../../api/services";

//components
import SelectInput, { Result } from "../Inputs/SelectInput";

interface GroupPickerProps {
	/**
	 * current value of the group picker
	 */
	value?: Group | Group[];

	/**
	 * function runs on GroupPicker change
	 */
	onChange?: (value: Group | Group[] | null) => void;

	/**
	 * label for the group picker
	 */
	label?: string;

	/**
	 * if true, the group picker will have a clear button
	 */
	clearable?: boolean;

	/**
	 * if true, the group picker will allow multiple selections
	 */
	isMultiSelect?: boolean;
	/**
	 * if true, the group picker will have a none value
	 */
	hasNoneValue?: boolean;
	disabled?: boolean;
	/**
	 * href of the title
	 */
	titleHref?: string;
	titleTooltip?: string;
	titleImage?: string;
}

/** @deprecated Use `SelectGroup` instead. */
const GroupPicker = ({
	value,
	onChange,
	label,
	clearable = false,
	isMultiSelect = false,
	hasNoneValue = false,
	disabled = false,
	titleHref,
	titleTooltip,
	titleImage,
}: GroupPickerProps) => {
	const { data: groups, isLoading } = useGroups();
	if (hasNoneValue && groups && groups?.length > 0 && groups[0]?.id !== "none") {
		groups?.unshift({ id: "none", name: "No Group" });
	}
	const results =
		groups?.map((color) => {
			return {
				label: color.name,
				value: color.id,
			};
		}) || [];

	const getValue = (group: Group | Group[]): Result | Result[] | undefined => {
		if (Array.isArray(group) && isMultiSelect) {
			return group.map((group) => ({ value: group.id, label: group.name }) as Result);
		} else if (!Array.isArray(group)) {
			return { value: group.id, label: group.name } as Result;
		}
	};

	const handleOnChange = (groupAsResult: Result | Result[]) => {
		if (onChange && groups) {
			if (Array.isArray(groupAsResult) && isMultiSelect) {
				const selectedGroups = groups.filter((group) =>
					groupAsResult.some((result) => result.value === group.id)
				);

				onChange(selectedGroups.map((group) => group));
			} else if (!Array.isArray(groupAsResult)) {
				const selectedGroup = groups.find((group) => group.id === groupAsResult?.value) || null;
				onChange(selectedGroup);
			}
		}
	};

	const getGroupPickerPlaceholder = () => {
		if (!isLoading && groups?.length === 0) {
			return "No groups found";
		} else if (isMultiSelect) {
			return "Select groups...";
		} else {
			return "Select a group...";
		}
	};

	const filterByNameOnly = (option: Result, rawInput: string) => {
		const searchInput = rawInput.toLowerCase();
		const optionLabel = option.label.toLowerCase();
		return optionLabel.includes(searchInput);
	};

	return (
		<div data-testid="groupPicker">
			<SelectInput
				label={label}
				inputId="group-picker"
				isLoading={isLoading}
				results={results}
				value={value && getValue(value)}
				onChange={handleOnChange}
				isClearable={clearable}
				isMultiSelect={isMultiSelect}
				placeholder={getGroupPickerPlaceholder()}
				disabled={disabled}
				titleHref={titleHref}
				titleTooltip={titleTooltip}
				titleImage={titleImage}
				filterOption={filterByNameOnly}
			/>
		</div>
	);
};

/** @deprecated Use `SelectGroup` instead. */
export default GroupPicker;
